import React, { useCallback } from 'react';
import {
  Typography,
  Grid,
  TableHead,
  TableBody,
  TableCell,
  Table,
  TableRow,
  Chip,
  makeStyles,
} from '@rbx/ui';
import { useTranslation } from '@rbx/intl';
import { ReleaseNoteContentType } from '@modules/utilities/pageBuild/content';
import StandardMarkdownNoMargin from '@modules/ui/markdown/components/StandardMarkdownNoMargin';
import ReleaseNoteType from '../enum/ReleaseNoteType';

export interface ReleaseNoteComponentProps {
  releaseNoteContents: ReleaseNoteContentType[];
  releaseNoteTypeList: ReleaseNoteType[];
  versionNumber: string;
}

const useReleaseNoteComponentStyles = makeStyles()((theme) => ({
  tableHeader: {
    backgroundColor: theme.palette.background.media,
  },
  tableStyle: {
    marginTop: 16,
    marginBottom: 32,
  },
  tableCell: {
    padding: 16,
    width: '100%',
  },
  status: {
    width: 90,
  },
  bottomTypographyStyle: {
    paddingTop: 64,
    paddingBottom: 16,
  },
  upperTypographyStyle: {
    paddingBottom: 16,
  },
  emptyContent: {
    marginBottom: 16,
  },
}));

export default function ReleaseNoteComponent({
  releaseNoteContents,
  releaseNoteTypeList,
  versionNumber,
}: ReleaseNoteComponentProps) {
  const {
    classes: {
      status,
      tableHeader,
      tableCell,
      tableStyle,
      bottomTypographyStyle,
      upperTypographyStyle,
      emptyContent,
    },
  } = useReleaseNoteComponentStyles();
  const { translate } = useTranslation();

  // NOTE (@mbae, 05/30/24): React 18 migration, couldn't figure out why row.ReleaseNotesText was complaining about types
  const getIteratorRows = useCallback(
    (row: ReleaseNoteContentType) => {
      return (
        <TableRow>
          <TableCell className={tableCell} size='small'>
            <Typography variant='body1'>
              {/* eslint-disable-next-line @typescript-eslint/no-explicit-any */}
              <StandardMarkdownNoMargin>{(row as any).ReleaseNotesText}</StandardMarkdownNoMargin>
            </Typography>
          </TableCell>
          <TableCell size='small' className={tableCell}>
            <Chip
              className={status}
              label={row.Status}
              color={row.Status === 'Pending' ? 'warning' : 'success'}
              sx={{
                borderRadius: '4px',
                color: 'white',
              }}
            />
          </TableCell>
        </TableRow>
      );
    },
    [status, tableCell],
  );

  const releaseNoteContentTable = useCallback(
    (releaseNoteType: ReleaseNoteType, index: number) => {
      return (
        <Grid item>
          <Typography
            variant='h2'
            component='h2'
            id={releaseNoteType}
            className={index === 0 ? upperTypographyStyle : bottomTypographyStyle}>
            {translate(`Heading.${releaseNoteType}`)}
          </Typography>
          <Grid className={tableStyle}>
            <Table>
              <TableHead className={tableHeader}>
                <TableRow>
                  <TableCell>{translate('Label.Note')}</TableCell>
                  <TableCell className={tableCell}>{translate('Label.Status')}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {releaseNoteContents
                  .filter((content) => content.ReleaseNotesType === releaseNoteType)
                  .map((row) => {
                    return getIteratorRows(row);
                  })}
              </TableBody>
            </Table>
          </Grid>
        </Grid>
      );
    },
    [
      bottomTypographyStyle,
      getIteratorRows,
      releaseNoteContents,
      tableHeader,
      tableStyle,
      tableCell,
      translate,
      upperTypographyStyle,
    ],
  );

  if (releaseNoteContents.length === 0) {
    return (
      <Grid className={emptyContent}>
        <Typography>{translate('Message.EmptyReleaseNoteText', { versionNumber })}</Typography>
      </Grid>
    );
  }
  return (
    <Grid>
      {releaseNoteTypeList.map((releaseNoteType, idx) => {
        return releaseNoteContentTable(releaseNoteType, idx);
      })}
    </Grid>
  );
}
